import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const { FiSearch, FiRefreshCcw, FiTrash2, FiExternalLink, FiUser, FiClock, FiActivity, FiCheckCircle } = FiIcons;

const AdminTestManagement = ({ refreshKey }) => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchTests = useCallback(async () => {
    setLoading(true);
    try {
      // Fazemos um join com app_profiles para pegar o email do usuário
      const { data, error } = await supabase
        .from('app_tests')
        .select(`
          *,
          profile:app_profiles(email, full_name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTests(data || []);
    } catch (err) {
      console.error("Erro ao buscar testes globais:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTests();
  }, [fetchTests, refreshKey]);

  const handleDeleteTest = async (test) => {
    if (!window.confirm(`Tem certeza que deseja desvincular este teste? Esta ação removerá o registro do usuário.`)) return;

    try {
      const { error } = await supabase
        .from('app_tests')
        .delete()
        .eq('id', test.id);

      if (error) throw error;
      
      // Opcional: Marcar a URL como disponível novamente no pool
      await supabase
        .from('app_virgin_pool')
        .update({ is_assigned: false })
        .eq('test_url', test.test_url);

      fetchTests();
      alert("Teste desvinculado com sucesso.");
    } catch (err) {
      alert("Erro ao excluir: " + err.message);
    }
  };

  const filteredTests = tests.filter(test => 
    test.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    test.profile?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    test.profile?.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="relative flex-1">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input 
            type="text"
            placeholder="Pesquisar por Título, Email ou Nome do Usuário..."
            className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none transition-all font-bold text-slate-900 text-xs"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button 
          onClick={fetchTests}
          className="flex items-center gap-2 px-6 py-3 bg-white border border-slate-100 rounded-2xl text-slate-500 font-black text-[10px] uppercase tracking-widest hover:bg-slate-50 transition-all shadow-sm"
        >
          <SafeIcon icon={FiRefreshCcw} className={loading ? 'animate-spin' : ''} /> Atualizar
        </button>
      </div>

      <div className="bg-white rounded-[2rem] border border-slate-100 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50/50 border-b border-slate-100">
                <th className="py-6 px-8 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Teste / Usuário</th>
                <th className="py-6 px-8 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">URL Completa</th>
                <th className="py-6 px-8 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Data</th>
                <th className="py-6 px-8 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Status</th>
                <th className="py-6 px-8 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 text-right">Controle</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filteredTests.length === 0 ? (
                <tr>
                  <td colSpan="5" className="py-20 text-center">
                    <p className="text-slate-300 font-black uppercase text-xs italic">Nenhum teste encontrado</p>
                  </td>
                </tr>
              ) : (
                filteredTests.map((test) => (
                  <tr key={test.id} className="hover:bg-slate-50/50 transition-colors group">
                    <td className="py-6 px-8">
                      <div className="font-black text-slate-900 text-sm uppercase italic leading-tight">{test.title}</div>
                      <div className="flex items-center gap-1.5 mt-1">
                        <SafeIcon icon={FiUser} className="text-blue-500 text-[10px]" />
                        <span className="text-[10px] text-slate-400 font-bold uppercase truncate max-w-[150px]">
                          {test.profile?.email || 'N/A'}
                        </span>
                      </div>
                    </td>
                    <td className="py-6 px-8 max-w-[200px]">
                      <div className="bg-slate-50 border border-slate-100 p-2 rounded-lg truncate text-[9px] font-mono text-slate-500">
                        {test.test_url}
                      </div>
                    </td>
                    <td className="py-6 px-8">
                      <div className="flex flex-col">
                        <span className="font-black text-slate-700 text-xs">{new Date(test.created_at).toLocaleDateString()}</span>
                        <span className="text-[9px] text-slate-400 font-bold uppercase">{new Date(test.created_at).toLocaleTimeString()}</span>
                      </div>
                    </td>
                    <td className="py-6 px-8">
                      <span className={`px-2.5 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-tighter shadow-sm border ${
                        test.status === 'completed' ? 'bg-green-50 text-green-600 border-green-100' :
                        test.status === 'active' ? 'bg-blue-50 text-blue-600 border-blue-100' :
                        'bg-amber-50 text-amber-600 border-amber-100'
                      }`}>
                        {test.status === 'completed' ? 'Concluído' : 
                         test.status === 'active' ? 'Ativo' : 'Pendente'}
                      </span>
                    </td>
                    <td className="py-6 px-8 text-right">
                      <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-all">
                        <a 
                          href={test.test_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="p-2.5 bg-white border border-slate-100 text-slate-400 hover:text-blue-600 hover:border-blue-200 rounded-xl shadow-sm transition-all"
                        >
                          <SafeIcon icon={FiExternalLink} />
                        </a>
                        <button 
                          onClick={() => handleDeleteTest(test)}
                          className="p-2.5 bg-white border border-slate-100 text-slate-400 hover:text-red-600 hover:border-red-200 rounded-xl shadow-sm transition-all"
                        >
                          <SafeIcon icon={FiTrash2} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminTestManagement;